import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TickerLinkProvider, useTickerLink } from '../contexts/TickerLinkContext';
import { isIndexSymbol } from '../utils/tickerDisplay';
import { PermissionGate } from '../components/PermissionGate';
import { HeroBar } from '../components/TickerDetail/HeroBar';
import { Overview } from '../components/TickerDetail/Overview';
import { GammaSection } from '../components/TickerDetail/GammaSection';
import { Financials } from '../components/TickerDetail/Financials';
import { Earnings } from '../components/TickerDetail/Earnings';
import { InstitutionalHolders } from '../components/TickerDetail/InstitutionalHolders';
import { Dividends } from '../components/TickerDetail/Dividends';
import { AnalystRatingsSection } from '../components/TickerDetail/AnalystRatingsSection';
import { CongressTradesSection } from '../components/TickerDetail/CongressTradesSection';
import { InsiderTradesSection } from '../components/TickerDetail/InsiderTradesSection';
import type { ToolType } from '../types/dashboard';

/**
 * Ticker Explorer — everything about one listed company.
 *
 * The page is a ticker and a set of sections. The react-grid-layout widget tab
 * that used to sit beside them is gone: those same tools are available on the
 * main dashboard, and duplicating them here meant a second layout to persist,
 * a second set of upgrade prompts, and a page that fetched every section's
 * data on each ticker change.
 *
 * ⚠️ **Only the active section mounts.** That is load-bearing rather than a
 * detail: several sections are backed by providers with a per-ACCOUNT
 * concurrency cap, so mounting all of them at once lets one user opening a
 * ticker starve requests belonging to everyone else on the platform. Keep the
 * conditional rendering below — do not swap it for CSS hiding.
 *
 * ⚠️ **Sections showing paid-tool data stay behind `PermissionGate`.** The
 * widget tab gated each of these; the sections render the same data from the
 * same feeds, so removing the gates would hand paid panels to unentitled
 * users. See `COMPANY_NAV`.
 */

// Last ticker viewed in the Explorer, so the page opens where it was left.
// localStorage rather than the backend's frontend_state: restoring has to be
// synchronous or the page paints its empty state first and then jumps.
const SYMBOL_KEY = 'fzn.explorer.symbol';

// Symbols are alphanumerics plus the separators used by class shares and
// indices (BRK.B, ^SPX). Anything else in storage is treated as absent —
// the value ends up in an API path, so it is never trusted blindly.
const SYMBOL_PATTERN = /^[A-Z0-9.^-]{1,12}$/;
const DEFAULT_EXPLORER_SYMBOL = 'NVDA';

function readStoredSymbol(): string {
  try {
    const stored = (localStorage.getItem(SYMBOL_KEY) ?? '').toUpperCase();
    // Indices are restored like any other symbol now that the page serves
    // them; only malformed values are dropped.
    if (!SYMBOL_PATTERN.test(stored)) return '';
    return stored;
  } catch {
    return '';
  }
}

function writeStoredSymbol(symbol: string): void {
  try {
    if (symbol) localStorage.setItem(SYMBOL_KEY, symbol);
    else localStorage.removeItem(SYMBOL_KEY);
  } catch {
    /* private mode — fall back to in-memory only */
  }
}

/**
 * The sections, and the tool each one is entitled by.
 *
 * `tool` carries the permission, and the mapping matches what the widget tab
 * gated on: Gamma is `gex-ray`, Analyst Ratings is `upgrades-downgrades`,
 * Congress + Insiders is `congress-trades`. Sections built from the company
 * fundamentals feed — financials, earnings, institutions — are ungated, as
 * they were before.
 */
const COMPANY_NAV: ReadonlyArray<{
  id: string;
  label: string;
  tool?: ToolType;
}> = [
  { id: 'overview', label: 'Overview' },
  { id: 'gamma', label: 'Gamma', tool: 'gex-ray' },
  { id: 'ratings', label: 'Analyst Ratings', tool: 'upgrades-downgrades' },
  { id: 'financials', label: 'Financials' },
  { id: 'flows', label: 'Congress + Insiders', tool: 'congress-trades' },
  { id: 'earnings', label: 'Earnings' },
  { id: 'institutions', label: 'Institutions' },
];

/**
 * The sections a cash index gets.
 *
 * Everything omitted here is company data an index does not have: no filings,
 * so no financials or earnings; no shares, so no institutional holders or
 * insider trades; no covering analysts, so no ratings. Those sections would
 * render empty against an index, and a page of empty sections reads as broken
 * rather than as the symbol being a different kind of thing — which is exactly
 * why indices used to be refused outright.
 *
 * Overview survives because its panels are price, volume, options flow and
 * gamma levels, all of which an index has. Its one company-shaped element is
 * the name in the hero bar, which already renders conditionally and simply
 * does not appear.
 *
 * ⚠️ Filtered from `COMPANY_NAV` rather than written out, so a section added
 * above is hidden from indices by default. Opting a new section IN is a
 * deliberate edit here; forgetting to opt one OUT would ship an empty tab.
 */
const INDEX_SECTIONS: ReadonlySet<string> = new Set(['overview', 'gamma']);

const NAV_FOR_INDEX = COMPANY_NAV.filter((s) => INDEX_SECTIONS.has(s.id));

type SectionId = string;

/**
 * Wraps a section in its permission gate, or renders it bare when ungated.
 *
 * `onRemove` is deliberately not passed: these are fixtures of the page rather
 * than dashboard tiles someone placed, so there is nothing to remove them from
 * and the gate's remove affordance would be a dead control.
 */
function Section({ tool, children }: {
  tool?: ToolType;
  children: React.ReactNode;
}): React.ReactElement {
  if (!tool) return <>{children}</>;
  return <PermissionGate toolType={tool}>{children}</PermissionGate>;
}

function CompanyInfoTab({ symbol }: { symbol: string }): React.ReactElement {
  const [activeSection, setActiveSection] = useState<SectionId>('overview');

  // An index gets the two sections it has data for. See NAV_FOR_INDEX.
  const nav = isIndexSymbol(symbol) ? NAV_FOR_INDEX : COMPANY_NAV;

  // ⚠️ Guard against a section that is no longer in the nav. Switching from
  // AAPL/Earnings to SPX leaves `activeSection` pointing at a tab that is not
  // rendered any more — without this the page shows a nav with nothing under
  // it, since every branch below tests an id that is no longer reachable.
  const active = nav.some((s) => s.id === activeSection) ? activeSection : 'overview';
  const current = nav.find((s) => s.id === active);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <HeroBar symbol={symbol} />

      {/* Horizontal section tabs, same treatment as the dashboard tabs. */}
      <nav className="flex-none flex items-center gap-1 px-4 bg-chrome border-b border-dark-border overflow-x-auto scrollbar-hide">
        {nav.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              active === id
                ? 'border-accent-yellow text-white'
                : 'border-transparent text-white/55 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* One section's data at a time — see the note on the page docblock. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <Section tool={current?.tool}>
          {active === 'overview' && <Overview symbol={symbol} />}
          {active === 'gamma' && <GammaSection symbol={symbol} />}
          {active === 'ratings' && <AnalystRatingsSection symbol={symbol} />}
          {active === 'financials' && (
            <>
              <Financials symbol={symbol} />
              <Dividends symbol={symbol} />
            </>
          )}
          {active === 'flows' && (
            <>
              <CongressTradesSection symbol={symbol} />
              {/* Gated with congress-trades above — the two travel together on
                  the same tool tier, as they did on the widget tab. */}
              <InsiderTradesSection symbol={symbol} />
            </>
          )}
          {active === 'earnings' && <Earnings symbol={symbol} />}
          {active === 'institutions' && <InstitutionalHolders symbol={symbol} />}
        </Section>
      </div>
    </div>
  );
}

function TickerDetailInner(): React.ReactElement {
  const { symbol: urlSymbol = '' } = useParams<{ symbol: string }>();

  // A ticker in the URL wins — a shared or bookmarked link has to show what
  // it points at. Only when there is none do we fall back to the remembered
  // one. Read once at mount via lazy initial state, not on every render.
  const [activeTicker, setActiveTicker] = useState(() => {
    // `/ticker/SPX` opens: an index gets the Overview and Gamma sections. It
    // used to drop to the empty state, back when the page was all-or-nothing.
    const fromUrl = urlSymbol.toUpperCase();
    if (fromUrl) return fromUrl;
    return readStoredSymbol() || DEFAULT_EXPLORER_SYMBOL;
  });
  const [tickerInput, setTickerInput] = useState(activeTicker);
  const { setLinkedTicker } = useTickerLink();

  // Remember the ticker and sync the linked-chart context. Covers every way
  // the ticker can change — typed, restored, or arrived at by URL.
  useEffect(() => {
    if (!activeTicker) return;
    writeStoredSymbol(activeTicker);
    setLinkedTicker(activeTicker);
  }, [activeTicker, setLinkedTicker]);

  useEffect(() => {
    document.title = activeTicker ? `${activeTicker} — Fuzion` : 'Ticker Explorer — Fuzion';
  }, [activeTicker]);

  // The company-name query lived here to label the header. HeroBar's quote
  // already carries the name, so fetching it again was a duplicate request.

  const handleTickerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Persisting and linking are handled by the effect above, which also
    // covers the restored-on-mount case this handler never sees.
    const sym = tickerInput.trim().toUpperCase();
    if (!sym) return;

    setActiveTicker(sym);
  };

  return (
    /* flex-1, not h-screen: the AppShell owns viewport height. */
    <div className="flex-1 min-h-0 bg-dark-bg flex flex-col overflow-hidden">
      <div className="flex-none bg-chrome border-b border-dark-border px-4 py-2 flex items-center gap-3">
        <h1 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
          Ticker Explorer
        </h1>
        <div className="w-px h-5 bg-dark-border" />
        <form onSubmit={handleTickerSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={tickerInput}
            onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
            className="w-24 px-2 py-1 bg-dark-surface border border-dark-border rounded text-accent-yellow font-numeric font-bold text-sm focus:outline-none focus:border-accent-yellow/50"
            placeholder="SYMBOL"
            autoFocus={!activeTicker}
          />
        </form>
        {/* An index used to be refused here with a message explaining why and
            pointing elsewhere for gamma. It now opens on the sections it has
            data for, so there is nothing to explain and nowhere else to send
            anyone. */}
        {isIndexSymbol(activeTicker) && (
          <span className="text-xs text-gray-400">
            Index — options data only
          </span>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTicker ? (
          <CompanyInfoTab symbol={activeTicker} />
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <img
                src="/logo-watermark.png"
                alt=""
                aria-hidden="true"
                className="w-16 h-16 mx-auto mb-4 opacity-10"
              />
              <p className="text-sm text-gray-400">Enter a ticker to explore</p>
              <p className="text-xs text-gray-500 mt-1">
                Gamma, ratings, financials, congress &amp; insider activity,
                earnings and institutions
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TickerDetailPage(): React.ReactElement {
  const { symbol: urlSymbol = '' } = useParams<{ symbol: string }>();
  return (
    /* TickerDashboardProvider removed with the widget tab — it fetched and
       saved ticker_dashboard layout state on every visit, which is pure
       overhead now that nothing consumes it. */
    <TickerLinkProvider>
      <TickerDetailInner key={urlSymbol} />
    </TickerLinkProvider>
  );
}
